import { Division } from "@models";
import { NewDivision, UpdatedDivision } from "@models/Division";
import { DivisionStatus } from "@models/DivisionStatus";
import axios from "axios";
import * as apiService from "../api.service";
import { DeleteDivisionWithVoucherError } from "./errors";
import { upsertAssetDivision } from "@service/assetDivision";
import _ from "lodash";
import { AssetDivision } from "@models/AssetDivision";
import { throwErrorWithMessages } from "@service/user/util";
import { DateString } from "@util/types";
import { upsertSachbezugDivision } from "@service/sachbezugDivision";
import { SachbezugDivision } from "@models/SachbezugDivision";

export async function getDivisionById(divisionId: string): Promise<Division> {
	const division = await apiService.GET(`division/${divisionId}`);
	return Division.fromJson(division.data);
}

export async function getDivisions(customerId: string): Promise<Division[]> {
	const params = new URLSearchParams();
	params.append("customerId", customerId);

	const response = await apiService.GET(`division`, {
		params,
	});
	return (response.data as any[]).map(Division.fromJson);
}

export async function addDivision(
	customerId: string,
	division: NewDivision
): Promise<Division> {
	const { providerSettings, isSachbezugFitnessEnabled, ...rest } = division;

	const response = await apiService.POST(
		`/customer/${customerId}/division`,
		rest
	);
	const newDivision = Division.fromJson(response.data);

	const promises: Promise<void>[] = [];

	// when changing this logic, keep in mind that asset-service assumes that a
	// DivisionUpdated event will be created when providerSettings are updated
	if (providerSettings.jobrad?.frameContractUUID) {
		promises.push(
			upsertAssetDivision(
				AssetDivision.fromJson({
					id: newDivision.divisionId,
					status: newDivision.status,
					providerSettings: providerSettings,
				})
			)
		);
	}

	// when changing this logic, keep in mind that sachbezug-service assumes that a
	// DivisionUpdated event will be created when extra settings are updated
	if (isSachbezugFitnessEnabled) {
		promises.push(
			upsertSachbezugDivision(
				SachbezugDivision.fromJson({
					divisionId: newDivision.divisionId,
					isFitnessEnabled: isSachbezugFitnessEnabled,
				})
			)
		);
	}

	return Division.fromJson({
		...newDivision,
		providerSettings,
		isSachbezugFitnessEnabled,
	});
}

type PutResponse = ReturnType<typeof apiService["PUT"]>;
export async function updateDivision(
	divisionId: string,
	division: UpdatedDivision,
	oldDivision: Division
): Promise<Division> {
	const { providerSettings, isSachbezugFitnessEnabled, ...rest } = division;

	const promises: [PutResponse, ...Promise<void>[]] = [
		apiService.PUT(`/division/${divisionId}`, rest),
	];

	if (!_.isEqual(providerSettings, oldDivision.providerSettings)) {
		promises.push(
			upsertAssetDivision(
				AssetDivision.fromJson({
					id: divisionId,
					status: oldDivision.status,
					providerSettings: providerSettings,
				})
			)
		);
	}

	if (isSachbezugFitnessEnabled !== oldDivision.isSachbezugFitnessEnabled) {
		promises.push(
			upsertSachbezugDivision(
				SachbezugDivision.fromJson({
					divisionId,
					isFitnessEnabled: isSachbezugFitnessEnabled,
				})
			)
		);
	}

	const [response] = await Promise.all(promises);

	return Division.fromJson({
		...response.data,
		providerSettings,
		isSachbezugFitnessEnabled,
	});
}

export async function updateDivisionStatus(
	divisionId: string,
	status: DivisionStatus,
	targetDate: DateString
): Promise<void> {
	if (status === DivisionStatus.Trial)
		throw new Error(
			`Unexpected updateDivisionStatus input: ${status} on division ${divisionId}`
		);

	const statusToPathMap: Record<DivisionStatus, string> = {
		[DivisionStatus.Active]: "activate",
		[DivisionStatus.Archived]: "archive",
		[DivisionStatus.Deactivated]: "deactivate",
		[DivisionStatus.Trial]: "unknown",
	};

	try {
		await apiService.PUT(`division/${divisionId}/${statusToPathMap[status]}`, {
			targetDate: targetDate,
		});
	} catch (error) {
		throwErrorWithMessages(error);
	}
}

export async function deleteDivision(
	customerId: string,
	divisionId: string
): Promise<number> {
	try {
		return (
			await apiService.DELETE(`/customer/${customerId}/division/${divisionId}`)
		).data;
	} catch (error) {
		console.log("deletedivisionError", error);
		if (
			axios.isAxiosError(error) &&
			error.response?.data.type === "division-with-vouchers"
		) {
			throw new DeleteDivisionWithVoucherError();
		}
		throw error;
	}
}
